// src/pages/PlatformAdminCalidadChatbot.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { 
  FaRobot, 
  FaThumbsUp, 
  FaThumbsDown, 
  FaSearch, 
  FaFilter, 
  FaEye, 
  FaTimes, 
  FaCheckCircle, 
  FaExclamationCircle,
  FaComments
} from 'react-icons/fa';
import PlatformBackButton from '../components/PlatformBackButton';
import './PlatformAdminCalidadChatbot.css';

const PlatformAdminCalidadChatbot = () => {
  const [calificaciones, setCalificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('todos'); // 'todos' | 'buena' | 'mala'
  const [filtroFracc, setFiltroFracc] = useState('todos');
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    setLoading(true);
    const colRef = collection(db, 'chatbot_calificaciones');
    const q = query(colRef, orderBy('fecha', 'desc'));

    const procesarSnap = (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Ordenamiento resiliente en cliente por fecha descendente
      data.sort((a, b) => {
        const tA = a.fecha?.toMillis ? a.fecha.toMillis() : (a.fecha?.seconds ? a.fecha.seconds * 1000 : (new Date(a.fecha || 0).getTime() || 0));
        const tB = b.fecha?.toMillis ? b.fecha.toMillis() : (b.fecha?.seconds ? b.fecha.seconds * 1000 : (new Date(b.fecha || 0).getTime() || 0));
        return tB - tA;
      });
      setCalificaciones(data);
      setLoading(false);
    };

    let unsubscribe = onSnapshot(q, procesarSnap, (error) => {
      console.warn('Aviso: error en query ordenado de calificaciones, aplicando fallback sin orden:', error);
      // Fallback resiliente sin orderBy (no requiere índice compuesto)
      unsubscribe = onSnapshot(colRef, procesarSnap, (err2) => {
        console.error('Error en fallback de calificaciones:', err2);
        setLoading(false);
      });
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  // Lista única de fraccionamientos para el selector
  const listaFraccionamientos = useMemo(() => {
    const setF = new Set();
    calificaciones.forEach(c => {
      if (c.fraccionamientoNombre) setF.add(c.fraccionamientoNombre);
    });
    return Array.from(setF);
  }, [calificaciones]);

  // Cálculos de KPIs
  const metricas = useMemo(() => {
    const total = calificaciones.length;
    const buenas = calificaciones.filter(c => c.calificacion === 'buena').length;
    const malas = calificaciones.filter(c => c.calificacion === 'mala').length;
    const tasaSatisfaccion = total > 0 ? ((buenas / total) * 100).toFixed(1) : '100.0';

    return { total, buenas, malas, tasaSatisfaccion };
  }, [calificaciones]);

  // Filtros aplicados
  const itemsFiltrados = useMemo(() => {
    return calificaciones.filter(item => {
      const matchTipo = filtroTipo === 'todos' || item.calificacion === filtroTipo;
      const matchFracc = filtroFracc === 'todos' || item.fraccionamientoNombre === filtroFracc;

      const term = searchTerm.toLowerCase().trim();
      const matchSearch = !term ||
        (item.pregunta || '').toLowerCase().includes(term) ||
        (item.respuesta || '').toLowerCase().includes(term) ||
        (item.nombreUsuario || '').toLowerCase().includes(term) ||
        (item.casa || '').toLowerCase().includes(term) ||
        (item.comentario || '').toLowerCase().includes(term);

      return matchTipo && matchFracc && matchSearch;
    });
  }, [calificaciones, filtroTipo, filtroFracc, searchTerm]);

  // Paginación
  const totalPages = Math.ceil(itemsFiltrados.length / ITEMS_PER_PAGE) || 1;
  const currentItems = itemsFiltrados.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const formatearFecha = (ts) => {
    if (!ts) return 'Reciente';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('es-MX', {
        timeZone: 'America/Mexico_City',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Fecha no disponible';
    }
  };

  return (
    <div className="platform-calidad-container">
      <div className="platform-calidad-header">
        <PlatformBackButton />
        <div className="header-titles">
          <h2><FaRobot className="header-icon" /> Calidad y Satisfacción del Asistente IA</h2>
          <p>Supervisa las calificaciones (👍 / 👎), comentarios y la precisión de respuestas emitidas a residentes y administradores.</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="calidad-kpi-grid">
        <div className="calidad-kpi-card blue">
          <div className="kpi-icon-box"><FaComments /></div>
          <div className="kpi-data">
            <span className="kpi-label">TOTAL EVALUACIONES</span>
            <strong className="kpi-val">{metricas.total}</strong>
          </div>
        </div>

        <div className="calidad-kpi-card green">
          <div className="kpi-icon-box"><FaThumbsUp /></div>
          <div className="kpi-data">
            <span className="kpi-label">RESPUESTAS ÚTILES</span>
            <strong className="kpi-val">{metricas.buenas}</strong>
          </div>
        </div>

        <div className="calidad-kpi-card red">
          <div className="kpi-icon-box"><FaThumbsDown /></div>
          <div className="kpi-data">
            <span className="kpi-label">POR CALIBRAR</span>
            <strong className="kpi-val">{metricas.malas}</strong>
          </div>
        </div>

        <div className="calidad-kpi-card purple">
          <div className="kpi-icon-box"><FaCheckCircle /></div>
          <div className="kpi-data">
            <span className="kpi-label">SATISFACCIÓN GLOBAL</span>
            <strong className="kpi-val">{metricas.tasaSatisfaccion}%</strong>
          </div>
        </div>
      </div>

      {/* Barra de Filtros */}
      <div className="calidad-filters-card">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por pregunta, residente, casa o motivo..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>

        <div className="filters-group">
          <div className="filter-select-wrap">
            <FaFilter className="filter-icon" />
            <select
              value={filtroTipo}
              onChange={(e) => { setFiltroTipo(e.target.value); setCurrentPage(1); }}
            >
              <option value="todos">Todas las calificaciones</option>
              <option value="buena">👍 Solo Buenas (Útiles)</option>
              <option value="mala">👎 Solo Malas (A revisar)</option>
            </select>
          </div>

          <div className="filter-select-wrap">
            <select
              value={filtroFracc}
              onChange={(e) => { setFiltroFracc(e.target.value); setCurrentPage(1); }}
            >
              <option value="todos">Todos los fraccionamientos</option>
              {listaFraccionamientos.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de Evaluaciones */}
      <div className="calidad-table-card">
        {loading ? (
          <div className="calidad-loading">Cargando evaluaciones del asistente en tiempo real...</div>
        ) : currentItems.length === 0 ? (
          <div className="calidad-empty">
            <FaRobot style={{ fontSize: '2.5rem', color: '#94a3b8', marginBottom: '10px' }} />
            <p>No se encontraron calificaciones con los filtros seleccionados.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="calidad-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Fraccionamiento</th>
                  <th>Usuario / Casa</th>
                  <th>Pregunta del Usuario</th>
                  <th>Evaluación</th>
                  <th>Comentario</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id} className={item.calificacion === 'mala' ? 'row-negative' : ''}>
                    <td className="col-fecha">{formatearFecha(item.fecha)}</td>
                    <td className="col-fracc"><strong>{item.fraccionamientoNombre || 'Domus Habita'}</strong></td>
                    <td className="col-user">
                      <div className="user-info-cell">
                        <span className="user-name">{item.nombreUsuario}</span>
                        <span className="user-casa">Casa {item.casa} ({item.rol || 'residente'})</span>
                      </div>
                    </td>
                    <td className="col-pregunta" title={item.pregunta}>
                      "{item.pregunta || 'Consulta interactiva'}"
                    </td>
                    <td className="col-rating">
                      {item.calificacion === 'buena' ? (
                        <span className="badge-rating positive"><FaThumbsUp /> Útil</span>
                      ) : (
                        <span className="badge-rating negative"><FaThumbsDown /> A revisar</span>
                      )}
                    </td>
                    <td className="col-comentario" title={item.comentario}>
                      {item.comentario ? item.comentario : <span className="text-muted">Sin comentario</span>}
                    </td>
                    <td className="col-action">
                      <button
                        type="button"
                        className="btn-inspect"
                        onClick={() => setSelectedItem(item)}
                        title="Inspeccionar pregunta y respuesta completa"
                      >
                        <FaEye /> Ver detalle
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="calidad-pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            >
              Anterior
            </button>
            <span>Página {currentPage} de {totalPages} ({itemsFiltrados.length} registros)</span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Modal de Inspección Detallada */}
      {selectedItem && (
        <div className="calidad-modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="calidad-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="calidad-modal-header">
              <h3>Detalle de Evaluación de IA</h3>
              <button className="btn-close-modal" onClick={() => setSelectedItem(null)}><FaTimes /></button>
            </div>
            <div className="calidad-modal-body">
              <div className="modal-meta-grid">
                <div>
                  <label>Fraccionamiento:</label>
                  <span>{selectedItem.fraccionamientoNombre || 'N/A'}</span>
                </div>
                <div>
                  <label>Usuario / Casa:</label>
                  <span>{selectedItem.nombreUsuario} (Casa {selectedItem.casa})</span>
                </div>
                <div>
                  <label>Calificación:</label>
                  <span>
                    {selectedItem.calificacion === 'buena' ? '👍 Útil (Positiva)' : '👎 Insatisfactoria (Negativa)'}
                  </span>
                </div>
                <div>
                  <label>Fecha de Consulta:</label>
                  <span>{formatearFecha(selectedItem.fecha)}</span>
                </div>
              </div>

              {selectedItem.comentario && (
                <div className="modal-comentario-box">
                  <label><FaExclamationCircle /> Comentario del Usuario:</label>
                  <p>{selectedItem.comentario}</p>
                </div>
              )}

              <div className="modal-chat-transcript">
                <div className="transcript-user">
                  <label>Pregunta del Usuario:</label>
                  <div className="bubble-usr">"{selectedItem.pregunta}"</div>
                </div>

                <div className="transcript-bot">
                  <label>Respuesta Entregada por el Asistente IA:</label>
                  <div className="bubble-bot-full">{selectedItem.respuesta}</div>
                </div>
              </div>
            </div>
            <div className="calidad-modal-footer">
              <button className="btn-modal-close-main" onClick={() => setSelectedItem(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformAdminCalidadChatbot;
